import React from 'react';
import { Button } from './Button';

export interface FormFooterProps {
  onCancel?: () => void;
  onContinue?: () => void;
  cancelLabel?: React.ReactNode;
  continueLabel?: React.ReactNode;
  cancelDisabled?: boolean;
  continueDisabled?: boolean;
  /** Shows a spinner on the Continue button and replaces its label */
  isLoading?: boolean;
  loadingLabel?: React.ReactNode;
  className?: string;
}

const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

/**
 * Shared modal/form footer with Cancel and Continue buttons.
 * Matches the `CreateEventForm` navigation footer styling.
 */
export const FormFooter: React.FC<FormFooterProps> = ({
  onCancel,
  onContinue,
  cancelLabel = 'Cancel',
  continueLabel = 'Continue →',
  cancelDisabled = false,
  continueDisabled = false,
  isLoading = false,
  loadingLabel,
  className,
}) => (
  <div
    className={`flex justify-between items-center pt-2 md:pt-4 border-t border-gray-200 shrink-0 mt-auto ${className ?? ''}`}
  >
    <Button
      variant="secondary"
      size="md"
      type="button"
      onClick={onCancel}
      disabled={cancelDisabled}
    >
      {cancelLabel}
    </Button>

    <Button
      variant="primary"
      size="md"
      type="button"
      onClick={onContinue}
      disabled={continueDisabled || isLoading}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <Spinner />
          {loadingLabel ?? continueLabel}
        </span>
      ) : (
        continueLabel
      )}
    </Button>
  </div>
);
